export const BENEFIT_LABELS: Record<string, string> = {
  aml: "Annual Medical Limit",
  alternative_medicine: "Alternative Medicine",
  alt_medicine: "Alternative Medicine",
  consultation_deductible: "Consultation Deductible",
  dental: "Dental",
  health_checkup: "Health Checkup",
  ip_copay: "Inpatient Copay",
  maternity: "Maternity",
  op_diagnostic_copay: "Outpatient Diagnostic Copay",
  optical: "Optical",
  pec: "Pre-existing & Chronic Conditions",
  pharmacy_copay: "Pharmacy Copay",
  pharmacy_limit: "Pharmacy Limit",
  physiotherapy: "Physiotherapy",
  psychiatric: "Psychiatric",
  room_type: "Room Type",
  territory: "Territorial Cover",
  wellness: "Wellness",
}

/**
 * Stored `benefitSelections` keys are camelCase (`ipCopay`, `roomType`),
 * while `BENEFIT_LABELS` and the DB use snake_case `factorType`/`benefitType`
 * (`ip_copay`, `room_type`). Normalize camelCase → snake_case so either form
 * resolves to the configured label.
 */
export function normalizeBenefitKey(value: string): string {
  return value
    .replace(/([A-Z])/g, "_$1")
    .toLowerCase()
    .replace(/^[_-]+/, "")
    .replace(/[-\s]+/g, "_")
}

export function getBenefitDisplayLabel(value: string): string {
  const normalized = normalizeBenefitKey(value)
  return (
    BENEFIT_LABELS[value] ??
    BENEFIT_LABELS[normalized] ??
    normalized
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase())
  )
}

/**
 * camelCase `benefitSelections` key → DB `adjustment_factors.factor_type`.
 * Inverse of FACTOR_TYPE_TO_SELECTION_KEY in components/pdf/regional/quote-data.ts
 * and FACTOR_TYPE_TO_KEY in apps/api/src/modules/pricing/quote-engine/benefit-map.ts.
 */
export const SELECTION_KEY_TO_FACTOR_TYPE: Record<string, string> = {
  aml: "aml",
  roomType: "room_type",
  territory: "territory",
  pec: "pec",
  pharmacyLimit: "pharmacy_limit",
  pharmacyCopay: "pharmacy_copay",
  ipCopay: "ip_copay",
  opDiagnosticCopay: "op_diagnostic_copay",
  consultationDeductible: "consultation_deductible",
  genericRestriction: "generic_restriction",
}

export type AdjustmentFactorCatalog = Array<{
  factorType: string
  options?: Array<{
    id?: string
    optionLabel?: string
    optionValue?: string
    multiplier?: number
    isDefault?: boolean
  }>
}>

export type OptionalBenefitDisplayRow = {
  optionLabel?: string | null
  limitValue?: string | number | null
  copayPct?: number | null
}

const CURRENCY_IN_LIMIT_PATTERN = /\b(AED|USD)\b/i

function limitValueHasCurrency(limitStr: string): boolean {
  return CURRENCY_IN_LIMIT_PATTERN.test(limitStr)
}

function formatLimitWithCurrency(
  limitValue: string | number,
  currency: string
): string {
  const limitStr = String(limitValue).trim()
  if (!limitStr) return ""

  if (limitValueHasCurrency(limitStr)) {
    return limitStr
  }

  const stripped = limitStr
    .replace(new RegExp(`^${currency}\\s+`, "i"), "")
    .trim()
  const isNumericOnly = /^[\d.,]+$/.test(stripped)

  return isNumericOnly ? `${currency} ${stripped}` : limitStr
}

function getLimitOnlyDisplayValue(
  limitValue: string | number | null | undefined
): string | null {
  if (limitValue == null || limitValue === "") return "Not covered"
  if (limitValue === 0 || limitValue === "0") return String(limitValue).trim()

  const normalized = String(limitValue).trim().toLowerCase()
  if (normalized === "not covered" || normalized === "nil") {
    return String(limitValue).trim()
  }

  return null
}

export function formatOptionalBenefitDisplayValue(
  row: OptionalBenefitDisplayRow | null | undefined,
  currency = "AED"
): string {
  if (!row) return "Not covered"
  if (row.optionLabel) return row.optionLabel.replace(/%/g, "")

  const rawLimit = row.limitValue
  const limitOnly = getLimitOnlyDisplayValue(rawLimit)
  if (limitOnly != null) return limitOnly

  const limitDisplay = formatLimitWithCurrency(rawLimit as string | number, currency)
  const copay = row.copayPct

  if (copay != null && copay > 0) {
    return `${copay}% Copay - ${limitDisplay}`
  }

  return limitDisplay
}

export function formatAdjustmentDisplayValue(value: unknown): string {
  if (value == null) return "-"
  if (typeof value === "string") {
    return value === "" ? "-" : value.replace(/%/g, "")
  }
  const row = value as OptionalBenefitDisplayRow
  if (row.optionLabel) return row.optionLabel.replace(/%/g, "")
  const limit = row.limitValue
  if (
    limit != null &&
    limit !== "" &&
    limit !== "0" &&
    limit !== 0 &&
    limit !== "Not Covered"
  ) {
    return String(limit)
  }
  if (row.copayPct != null) return `${row.copayPct}%`
  return "Not covered"
}

/**
 * Resolve the human-readable optionLabel for a stored benefit selection.
 * Individual quotes store raw optionValue strings ("aml", "0", "private"),
 * so without the adjustment-factors catalog the UI shows internal codes
 * instead of labels like "Covered up to AML".
 */
export function resolveAdjustmentDisplayValue(
  key: string,
  selection: unknown,
  adjustmentFactors: AdjustmentFactorCatalog | undefined
): string {
  const row =
    selection !== null && typeof selection === "object" && !Array.isArray(selection)
      ? (selection as {
          id?: string
          factorType?: string
          optionLabel?: string
          optionValue?: string
        })
      : undefined
  const factorType =
    row?.factorType ?? SELECTION_KEY_TO_FACTOR_TYPE[key] ?? normalizeBenefitKey(key)

  if (row?.optionLabel) return row.optionLabel.replace(/%/g, "")

  const factor = adjustmentFactors?.find((f) => f.factorType === factorType)
  const options = factor?.options ?? []
  const match =
    options.find(
      (o) =>
        typeof selection === "string" &&
        (o.optionValue === selection || o.id === selection)
    ) ??
    options.find(
      (o) => row?.optionValue != null && o.optionValue === row.optionValue
    ) ??
    options.find((o) => row?.id != null && o.id === row.id)

  if (match?.optionLabel) return match.optionLabel.replace(/%/g, "")

  return formatAdjustmentDisplayValue(selection)
}
